import { easeInOut, motion } from 'motion/react'
import { Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'

const pulseAnimation = {
  initial: { scale: 1 },
  animate: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-12, 12, -12],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

export const CTASection = () => (
  <div className="relative overflow-hidden bg-[#001871] font-roboto text-white">
    {/* Enhanced texture overlay with radial gradient */}
    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light scale-125"></div>
    <div className="absolute inset-0 bg-linear-to-tr from-[#001871] via-[#001871]/95 to-[#001871]/90"></div>

    {/* Animated background shapes */}
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 0.15, scale: 1 }}
      transition={{ duration: 2 }}
      className="absolute left-0 top-0 size-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white blur-3xl"
    />
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 0.1, scale: 1 }}
      transition={{ duration: 2, delay: 0.5 }}
      className="absolute bottom-0 right-0 size-[800px] translate-x-1/2 translate-y-1/2 rounded-full bg-white blur-3xl"
    />

    {/* Floating decorative elements */}
    <motion.div
      variants={floatingAnimation}
      initial="initial"
      animate="animate"
      className="absolute left-20 top-1/3 hidden size-28 rotate-12 rounded-2xl bg-white/10 shadow-lg backdrop-blur-lg lg:block"
    />
    <motion.div
      variants={floatingAnimation}
      initial="initial"
      animate="animate"
      transition={{ delay: 1 }}
      className="absolute bottom-1/3 right-20 hidden size-28 -rotate-12 rounded-2xl bg-white/10 shadow-lg backdrop-blur-lg lg:block"
    />

    <div className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="space-y-8"
          >
            <motion.div
              variants={glowAnimation}
              initial="initial"
              animate="animate"
              className="inline-flex items-center rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
            >
              Get Started Today
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
              className="text-4xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-5xl"
            >
              Ready to Transform Your Literature Review Process?
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
              className="text-xl leading-8 text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-2xl"
            >
              Join leading medical device companies using SOTA Navigator to streamline their
              research
            </motion.p>
          </motion.div>
        </div>

        <motion.div
          className="mt-16 flex flex-col items-center gap-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <motion.div
            variants={pulseAnimation}
            initial="initial"
            animate="animate"
            className="flex justify-center"
          >
            <Button
              size="lg"
              className="flex items-center gap-3 rounded-full bg-white px-12 py-8 text-lg text-[#001871] shadow-xl transition-all duration-300 hover:bg-white/90 hover:shadow-2xl"
            >
              <Calendar className="size-6" />
              Schedule a Demo
            </Button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-sm text-white/70"
          >
            No credit card required • Free consultation • Live demo with our experts
          </motion.p>
        </motion.div>
      </div>
    </div>
  </div>
)
